import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NativePushService } from '@core/services/native-shell/native-push.service';

import {
  CHAT_CONVERSATION_LINK_ADAPTER,
  ChatConversationLinkAdapter,
  ChatLauncherController,
  ChatSystemNotificationAdapter,
  ChatSystemNotificationEnvironment,
  ChatSystemNotificationPayload,
  ChatSystemNotificationPermission,
} from 'ias-ppr-mt-chat-client';

interface StandaloneNavigator extends Navigator {
  readonly standalone?: boolean;
}

@Injectable()
export class PwaChatSystemNotificationAdapter implements ChatSystemNotificationAdapter {
  private readonly document = inject(DOCUMENT);
  private readonly conversationLinks = inject<ChatConversationLinkAdapter>(
    CHAT_CONVERSATION_LINK_ADAPTER,
  );
  private readonly launcher = inject(ChatLauncherController);
  private readonly nativePush = inject(NativePushService);

  public getPermission(): ChatSystemNotificationPermission {
    if (this.nativePush.present) return this.nativePush.permission();
    const NotificationApi = this.document.defaultView?.Notification;
    return NotificationApi?.permission ?? 'unsupported';
  }

  public getEnvironment(): ChatSystemNotificationEnvironment {
    if (this.nativePush.present) return 'installed-app';
    const window = this.document.defaultView;
    const standaloneDisplay = window?.matchMedia(
      '(display-mode: standalone)',
    ).matches;
    const standaloneNavigator = (window?.navigator as StandaloneNavigator)
      ?.standalone;

    return standaloneDisplay || standaloneNavigator
      ? 'installed-app'
      : 'browser';
  }

  public async requestPermission(): Promise<ChatSystemNotificationPermission> {
    if (this.nativePush.present) return this.nativePush.requestPermission();
    const NotificationApi = this.document.defaultView?.Notification;
    return NotificationApi
      ? NotificationApi.requestPermission()
      : 'unsupported';
  }

  public async show(
    notification: ChatSystemNotificationPayload,
  ): Promise<void> {
    if (this.nativePush.present) return;
    if (this.getPermission() !== 'granted' || this.isApplicationFocused()) {
      return;
    }

    const href = this.createMessageHref(notification);
    const registration = await this.getServiceWorkerRegistration();

    if (registration) {
      await registration.showNotification(notification.title, {
        body: notification.body,
        icon: '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/icon-96x96.png',
        tag: `mt-chat-message-${notification.messageId}`,
        data: {
          onActionClick: {
            default: {
              operation: 'navigateLastFocusedOrOpen',
              url: href,
            },
          },
        },
      });
      return;
    }

    this.showBrowserNotification(notification);
  }

  private createMessageHref(
    notification: ChatSystemNotificationPayload,
  ): string {
    const conversationHref =
      this.conversationLinks.getNavigationHref(
        notification.conversationReference,
      ) ?? '/chat';
    const url = new URL(conversationHref, this.document.baseURI);
    url.searchParams.set('messageId', notification.messageId);
    return url.href;
  }

  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    const serviceWorker = this.document.defaultView?.navigator.serviceWorker;

    if (!serviceWorker) {
      return null;
    }

    try {
      return await serviceWorker.getRegistration();
    } catch {
      return null;
    }
  }

  private showBrowserNotification(
    notification: ChatSystemNotificationPayload,
  ): void {
    const NotificationApi = this.document.defaultView?.Notification;
    if (!NotificationApi) {
      return;
    }

    const browserNotification = new NotificationApi(notification.title, {
      body: notification.body,
      icon: '/assets/icons/icon-192x192.png',
      tag: `mt-chat-message-${notification.messageId}`,
    });
    browserNotification.onclick = () => {
      this.document.defaultView?.focus();
      this.launcher.openMessage(
        notification.conversationReference,
        notification.messageId,
      );
      browserNotification.close();
    };
  }

  private isApplicationFocused(): boolean {
    return (
      this.document.visibilityState === 'visible' && this.document.hasFocus()
    );
  }
}
